/**
 * Diagnostics data (UX-SPEC §9): every value the Diagnostics view shows, the report's diagnostics section and the
 * "Copy diagnostics" text, as ready strings. Pure, no DOM, no engine imports (the main thread uses it too).
 */
import type { DocHygiene } from '../engine/document/types.ts';
import type { SegmentIndex } from '../engine/ensemble/segments-index.ts';
import { OVERVIEW } from '../web/strings.ts';
import type { SlimSegment, ViewModel } from './viewmodel.ts';

export interface DiagSection {
	id: string;
	title: string;
	/** A definition list. */
	rows?: Array<[label: string, value: string]>;
	table?: { head: string[]; rows: string[][] };
	/** Free lines (notes, verbatim engine messages), shown after the rows and the table. */
	lines?: string[];
}

export interface DiagOptions {
	/** The browser's measured wall time from Run to first overview paint, in milliseconds. */
	wallMs?: number;
}

const f1 = (x: number): string => x.toFixed(1);
const f2 = (x: number): string => x.toFixed(2);
const f3 = (x: number): string => x.toFixed(3);
const pct1 = (x: number): string => `${(x * 100).toFixed(1)}%`;

/** 12345 → "12,345" (no locale dependency). */
export function thousands(n: number): string {
	const s = String(Math.round(n));
	return s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

const plural = (n: number, one: string, many = `${one}s`): string => (n === 1 ? one : many);

/** A per-1,000-characters rate with enough digits to be non-zero when the count is (0.04, 0.4, 12.3). */
function perThousand(x: number): string {
	if (x === 0) return '0';
	if (x >= 0.1) return f1(x);
	return x >= 0.005 ? f2(x) : '<0.01';
}

/**
 * The one plain line for the intake hygiene indicator (ENGINE-SPEC §2.1 N7, N8), or null when nothing was normalised
 * (`vm.ensemble.hygiene` is absent then). Evidence about the text; never a channel score or a model input.
 */
export function hygieneLine(h: DocHygiene | undefined): string | null {
	if (!h || h.invisibleRemoved + h.lookalikesMapped === 0) return null;
	return OVERVIEW.hygiene(
		thousands(h.invisibleRemoved),
		thousands(h.lookalikesMapped),
		perThousand(h.invisiblePer1k),
		perThousand(h.lookalikesPer1k)
	);
}

/** Ordinals (0-based) as a compact 1-based range list: [0,1,2,4] → "1–3, 5". */
function ordinalRanges(ords: number[]): string {
	const out: string[] = [];
	let i = 0;
	while (i < ords.length) {
		let j = i;
		while (j + 1 < ords.length && (ords[j + 1] as number) === (ords[j] as number) + 1) j++;
		const a = (ords[i] as number) + 1;
		const b = (ords[j] as number) + 1;
		out.push(a === b ? String(a) : `${a}–${b}`);
		i = j + 1;
	}
	return out.join(', ');
}

function methodCounts(list: SlimSegment[]): string {
	if (list.length === 0) return 'none';
	const n = new Map<string, number>();
	for (const s of list) n.set(s.ciMethod, (n.get(s.ciMethod) ?? 0) + 1);
	return [...n].map(([m, c]) => `${m} × ${c}`).join(', ');
}

function unitWord(kind: string | null | undefined, n: number): string {
	if (kind === 'chapter' || kind === 'scene' || kind === 'paragraph') return plural(n, kind);
	return plural(n, 'unit');
}

/** Channels excluded per segment, grouped so that segments with the same exclusions share one line. */
function exclusionLines(label: string, list: SlimSegment[]): string[] {
	const groups = new Map<string, number[]>();
	for (const s of list) {
		if (s.channelsExcluded.length === 0) continue;
		const key = s.channelsExcluded.map((c) => `${c.id} (${c.reason})`).join('; ');
		const g = groups.get(key);
		if (g) g.push(s.ordinal);
		else groups.set(key, [s.ordinal]);
	}
	return [...groups].map(
		([key, ords]) => `${label} ${ordinalRanges(ords)}: channels excluded: ${key}`
	);
}

const segmentLabel = (kind: SegmentIndex['kind']): string =>
	kind === 'joint' ? 'Joint segment' : kind === 'chapter' ? 'Chapter' : 'Scene';

export function diagnosticsSections(vm: ViewModel, opts: DiagOptions = {}): DiagSection[] {
	const { meta, ensemble: ens } = vm;
	const dg = ens.diagnostics;
	const agg = ens.aggregate;
	const name = new Map(vm.channels.map((c) => [c.id, c.name]));
	const label = (id: string): string => {
		const n = name.get(id as never);
		return n ? `${id} ${n}` : id;
	};
	const sections: DiagSection[] = [];

	// Model
	const ci = ens.calibratedIndex;
	const modelRows: Array<[string, string]> = [
		['Model id', ens.model.id],
		['Calibrated', ens.model.calibrated ? 'yes' : 'no'],
	];
	if (ci) {
		modelRows.push(
			['Calibrated scope', 'the machine-authorship index only'],
			['Calibrated index', ci.index === null ? 'not available' : f1(ci.index)],
			['Length group', ci.group ?? `none (fewer than ${thousands(ci.floorWords)} prose words)`],
			['Prose words (length group input)', thousands(ci.proseWords)],
			['Model logit', ci.logit === null ? 'not evaluated' : ci.logit.toFixed(3)],
			['Status at the operating point', ci.statusPhrase],
			...(ci.interval
				? ([
						['Text-sampling interval', ci.interval.phrase],
						[
							'Text-sampling interval (model logit)',
							`${ci.interval.logit[0].toFixed(3)} to ${ci.interval.logit[1].toFixed(3)}`,
						],
						[
							'Text-sampling interval basis',
							`${ci.interval.B} resamples of ${ci.interval.units} ${ci.interval.unit}s, ${Math.round(ci.interval.level * 100)}% percentile interval`,
						],
						['Text-sampling interval meaning', OVERVIEW.intervalMeaning],
					] as Array<[string, string]>)
				: ci.interval === null && ci.intervalReason
					? ([['Text-sampling interval', `not available: ${ci.intervalReason}`]] as Array<
							[string, string]
						>)
					: []),
			[
				'Reference point',
				`${ci.referenceName}: ${f1(ci.referenceIndex)} (reference prior ${f2(ci.referencePrior)})`,
			],
			['Context points exceeded', ci.contextPoints.length ? ci.contextPoints.join(', ') : 'none']
		);
		if (ci.reason) modelRows.push(['Calibrated index note', ci.reason]);
	}
	modelRows.push(
		['Model kind', ens.model.kind],
		['Lexicon version', ens.model.lexiconVersion],
		['Engine version', meta.engineVersion],
		['Caveat', ens.caveat]
	);
	sections.push({ id: 'model', title: 'Model', rows: modelRows });

	// Document
	const format = meta.dispatchedFormat
		? `${meta.format} (read as ${meta.dispatchedFormat})`
		: meta.format;
	const docRows: Array<[string, string]> = [
		['Name', meta.name],
		['Source kind', meta.sourceKind],
		['Format', format],
		['Bytes', thousands(meta.bytes)],
		['Intake words', thousands(meta.words)],
		['Prose words', thousands(meta.proseWords)],
		['Paragraphs', thousands(meta.paragraphs)],
		['Chapters', thousands(meta.chapters)],
		['Scenes', thousands(meta.scenes)],
		['Language', `${meta.language.code} (confidence ${f2(meta.language.confidence)})`],
	];
	if (meta.pages !== undefined) docRows.push(['Pages', thousands(meta.pages)]);
	if (meta.finalUrl) docRows.push(['Final URL', meta.finalUrl]);
	docRows.push(['Analysed at', meta.analysedAt]);
	docRows.push(['Document hash', meta.docHash]);
	docRows.push(['Intake warnings', meta.warnings.length ? String(meta.warnings.length) : 'none']);
	const doc: DiagSection = { id: 'document', title: 'Document', rows: docRows };
	if (meta.warnings.length) doc.lines = [...meta.warnings];
	const hyg = hygieneLine(ens.hygiene);
	if (hyg) doc.lines = [...(doc.lines ?? []), hyg];
	sections.push(doc);

	// Intervals
	const rs = dg.resampling;
	const resampling =
		rs.method === 'unit-bootstrap'
			? `unit bootstrap over ${thousands(rs.units)} ${unitWord(rs.unit, rs.units)}, B = ${thousands(rs.B)}`
			: 'delta method (too few resampling units for a bootstrap)';
	sections.push({
		id: 'intervals',
		title: 'Intervals',
		rows: [
			['Resampling', resampling],
			['Method', rs.method],
			['Resampling unit', rs.unit ?? 'none'],
			['Units', thousands(rs.units)],
			['Replicates B', thousands(rs.B)],
			['Level', `${Math.round(ens.level * 100)}%`],
			[
				ci ? 'Seed-rules composite interval' : 'Index interval',
				`${f1(ens.ci90[0])}–${f1(ens.ci90[1])}`,
			],
		],
	});

	// Segmentation
	const joint = ens.segments.joint;
	const units = ens.segments.units;
	const unitKind = units[0]?.kind;
	sections.push({
		id: 'segmentation',
		title: 'Segmentation',
		rows: [
			['Joint segments', String(dg.jointSegments)],
			[
				'Units',
				unitKind
					? `${dg.units} ${unitWord(unitKind === 'joint' ? null : unitKind, dg.units)}`
					: '0',
			],
			['Interval methods, joint segments', methodCounts(joint)],
			['Interval methods, units', methodCounts(units)],
		],
		table: {
			head: [
				'Segment',
				'Paragraphs',
				'Words',
				'Index',
				'90% interval',
				'Interval method',
				'Channels used',
			],
			rows: joint.map((s) => [
				String(s.ordinal + 1),
				`${s.range.paraStart + 1}–${s.range.paraEnd}`,
				thousands(s.words),
				f1(s.index),
				`${f1(s.ci90[0])}–${f1(s.ci90[1])}`,
				s.ciMethod,
				String(s.channelsUsed.length),
			]),
		},
	});

	// Evidence
	const ev = ens.evidence;
	const perChannel = vm.channels
		.filter((c) => (ev.byChannel[c.id] ?? 0) > 0)
		.map((c) => [c.id, c.name, thousands(ev.byChannel[c.id] ?? 0)]);
	sections.push({
		id: 'evidence',
		title: 'Evidence',
		rows: [
			['Total spans', thousands(ev.spans)],
			['Channels with spans', `${ev.channelsWithSpans} of ${vm.channels.length}`],
			[
				'Text inside evidence spans',
				`${pct1(ev.share)} (${thousands(ev.coveredChars)} of ${thousands(ev.totalChars)} characters)`,
			],
		],
		table: { head: ['Channel', 'Name', 'Spans'], rows: perChannel },
		lines: [
			'Text inside evidence spans is the share of the text covered by at least one span. It is not the evidence coverage in the headline, which measures text length and channel data weights.',
		],
	});

	// Abstentions
	const abs: DiagSection = { id: 'abstentions', title: 'Abstentions' };
	if (ens.abstained.length) {
		abs.table = {
			head: ['Channel', 'Reason'],
			rows: ens.abstained.map((a) => [label(a.id), a.reason]),
		};
	} else abs.lines = ['No channel abstained.'];
	const excl = [
		...exclusionLines('Joint segment', joint),
		...exclusionLines(unitKind && unitKind !== 'joint' ? segmentLabel(unitKind) : 'Unit', units),
	];
	if (excl.length) abs.lines = [...(abs.lines ?? []), ...excl];
	sections.push(abs);

	// Rules
	const ruleRows: string[][] = [
		['L (sum of weighted channel evidence)', f3(agg.L)],
		['Index before the assistance floor', f1(agg.indexBeforeAssist)],
		['Assistance floor', agg.assistFloor === null ? 'not applied' : f1(agg.assistFloor)],
		['Assisted rule fired', agg.assisted ? 'yes' : 'no'],
		['D (deep-channel evidence sum)', f3(agg.D)],
		['D, structural part', f3(agg.Dstruct)],
		['Surface discount factor', f3(agg.surfDiscount)],
		['Scrub factor', f3(agg.scrub)],
		['Choreographed-structure condition', agg.choreographed ? 'yes' : 'no'],
		['Stitched-evidence condition', agg.stitchedEv ? 'yes' : 'no'],
		['Weak-agreement count', String(agg.weakAgree)],
		['One-way negative sum', f3(agg.oneNull)],
		['One-way negative scale', f3(agg.nScale)],
		['Second one-way score', f1(agg.secondOneWay)],
		['Weight denominator', f3(agg.den)],
	];
	sections.push({
		id: 'rules',
		title: 'Rules',
		rows: [['Assistance floor share', pct1(dg.assistFloorShare)]],
		table: { head: ['Rule value', 'Value'], rows: ruleRows },
		lines: [
			'Assistance floor share is the share of resamples in which the assistance floor bound the index.',
			'The rule values above are the seed rules carried over unchanged; C3 ablates each rule.',
		],
	});

	// Contributions (the per-channel part of the index sum L)
	if (agg.contributions.length) {
		sections.push({
			id: 'contributions',
			title: 'Contribution to the index sum L',
			table: {
				head: ['Channel', 'Evidence strength', 'Data weight κ', 'Contribution to the index sum L'],
				rows: agg.contributions.map((c) => [label(c.id), f1(c.score), f2(c.kappa), f2(c.adjusted)]),
			},
		});
	}

	// Length-dependent and withheld channels
	const lengthNote = (id: string): string =>
		vm.channels.find((c) => c.id === id)?.notes.find((n) => n.startsWith('length-dependent')) ??
		'length-dependent';
	const lw: string[][] = [
		...dg.lengthDependent.map((id) => [label(id), 'Length-dependent', lengthNote(id)]),
		...ens.hotspots.withheld.map((w) => [label(w.id), 'Flags withheld', w.reason]),
	];
	const lwSection: DiagSection = {
		id: 'length-withheld',
		title: 'Length-dependent and withheld channels',
	};
	if (lw.length) lwSection.table = { head: ['Channel', 'Status', 'Reason'], rows: lw };
	else lwSection.lines = ['No channel is length-dependent or has its flags withheld.'];
	sections.push(lwSection);

	// Messages
	sections.push({
		id: 'messages',
		title: 'Messages',
		lines: dg.messages.length ? [...dg.messages] : ['No messages.'],
	});

	// Timings
	const timeRows: string[][] = Object.entries(ens.timings).map(([k, v]) => [k, f1(v)]);
	const trows: Array<[string, string]> = [['Intake parse', `${f1(meta.parseMs)} ms`]];
	if (opts.wallMs !== undefined)
		trows.push(['Run to first overview paint (browser wall time)', `${thousands(opts.wallMs)} ms`]);
	sections.push({
		id: 'timings',
		title: 'Timings',
		rows: trows,
		table: { head: ['Engine stage', 'Milliseconds'], rows: timeRows },
	});

	return sections;
}

/** A plain-text table: columns padded to their widest cell, two spaces apart, a dashed rule under the head. */
export function textTable(head: string[], rows: string[][], rightAlign: boolean[] = []): string[] {
	const w = head.map((h) => h.length);
	for (const r of rows)
		for (let i = 0; i < head.length; i++) w[i] = Math.max(w[i] as number, (r[i] ?? '').length);
	const line = (cells: string[]): string =>
		cells
			.map((c, i) => (rightAlign[i] ? c.padStart(w[i] as number) : c.padEnd(w[i] as number)))
			.join('  ')
			.trimEnd();
	return [line(head), w.map((n) => '-'.repeat(n)).join('  '), ...rows.map(line)];
}

/** The plain text copied by "Copy diagnostics" (UX-DIAG-02). */
export function diagnosticsText(sections: DiagSection[]): string {
	const out: string[] = [];
	for (const s of sections) {
		out.push(s.title, '-'.repeat(s.title.length));
		if (s.rows) for (const [k, v] of s.rows) out.push(`${k}: ${v}`);
		if (s.table) {
			if (s.rows) out.push('');
			if (s.table.rows.length) out.push(...textTable(s.table.head, s.table.rows));
			else out.push('(none)');
		}
		if (s.lines) {
			if (s.rows || s.table) out.push('');
			out.push(...s.lines);
		}
		out.push('');
	}
	return `${out.join('\n').trimEnd()}\n`;
}
